import { useEffect } from 'react';
import { DollarSign, Database, TrendingUp, Activity, RefreshCw, AlertCircle } from 'lucide-react';
import { useAdminStore } from '../../store/adminStore';
import type { ScrapingDashboardData } from '../../store/adminStore';

// ── Helpers ────────────────────────────────────────────────────────────

function formatCost(amount: number): string {
  return `$${amount.toFixed(3)}`;
}

function formatPercent(count: number, total: number): string {
  if (total === 0) return '0%';
  return `${Math.round((count / total) * 100)}%`;
}

function formatStatus(status: string): { label: string; className: string } {
  switch (status) {
    case 'SUCCEEDED':
      return { label: 'OK', className: 'bg-green-100 text-green-800 dark:bg-green-900/30 dark:text-green-300' };
    case 'RUNNING':
      return { label: 'Running', className: 'bg-blue-100 text-blue-800 dark:bg-blue-900/30 dark:text-blue-300' };
    case 'FAILED':
      return { label: 'Failed', className: 'bg-red-100 text-red-800 dark:bg-red-900/30 dark:text-red-300' };
    case 'ABORTED':
      return { label: 'Aborted', className: 'bg-gray-100 text-gray-800 dark:bg-gray-700 dark:text-gray-300' };
    default:
      return { label: status, className: 'bg-gray-100 text-gray-800 dark:bg-gray-700 dark:text-gray-300' };
  }
}

function formatRunType(type: string, platform: string | null): string {
  if (type === 'twitter') return 'Twitter/X';
  if (type === 'facebook') return platform ? `FB: ${platform}` : 'Facebook';
  if (type === 'enrichment') return 'Enrichment';
  if (type === 'screenshot') return 'Screenshots';
  return type;
}

function formatDateTime(iso: string): string {
  const d = new Date(iso);
  return d.toLocaleDateString('en-US', { month: 'short', day: 'numeric' }) +
    ' ' + d.toLocaleTimeString('en-US', { hour: 'numeric', minute: '2-digit' });
}

const PLATFORM_LABELS: Record<string, string> = {
  VERTICAL_RAISE: 'Vertical Raise',
  SNAP_RAISE: 'Snap Raise',
  ETEAM_SPONSOR: 'eTeamSponsor',
  SCHOOL_FUNDR: 'SchoolFundr',
};

const SOURCE_LABELS: Record<string, string> = {
  CSV_IMPORT: 'CSV Import',
  TWITTER: 'Twitter/X',
  FACEBOOK: 'Facebook',
  MANUAL: 'Manual',
  INSTAGRAM: 'Instagram',
};

// ── Component ──────────────────────────────────────────────────────────

export const CompetitorScrapingTab = () => {
  const { scrapingData, isLoading, error, period, fetchScrapingData, setPeriod } = useAdminStore();

  useEffect(() => {
    fetchScrapingData();
  }, [fetchScrapingData]);

  if (error) {
    return (
      <div className="bg-red-50 dark:bg-red-900/30 border border-red-200 dark:border-red-800 rounded-lg p-4 flex items-start gap-3">
        <AlertCircle className="w-5 h-5 text-red-500 dark:text-red-400 flex-shrink-0 mt-0.5" />
        <div>
          <p className="text-sm text-red-700 dark:text-red-300">{error}</p>
          <button onClick={fetchScrapingData} className="text-sm text-red-600 hover:text-red-800 dark:text-red-400 mt-1">
            Retry
          </button>
        </div>
      </div>
    );
  }

  const d = scrapingData;

  return (
    <div className="space-y-6">
      {/* Period selector + refresh */}
      <div className="flex items-center justify-between">
        <div className="flex items-center gap-2">
          <label className="text-sm text-gray-500 dark:text-gray-400">Period:</label>
          <select
            value={period}
            onChange={(e) => setPeriod(Number(e.target.value) as 7 | 14 | 30 | 90)}
            className="input w-auto text-sm"
          >
            <option value={7}>Last 7 days</option>
            <option value={14}>Last 14 days</option>
            <option value={30}>Last 30 days</option>
            <option value={90}>Last 90 days</option>
          </select>
        </div>
        <button
          onClick={fetchScrapingData}
          disabled={isLoading}
          className="btn-secondary text-sm flex items-center gap-1.5"
        >
          <RefreshCw className={`w-4 h-4 ${isLoading ? 'animate-spin' : ''}`} />
          Refresh
        </button>
      </div>

      {/* Stats cards */}
      <div className="grid grid-cols-2 md:grid-cols-4 gap-4">
        <StatCard
          icon={<Database className="w-5 h-5 text-blue-500" />}
          label="Total Fundraisers"
          value={d ? d.health.totalActive.toLocaleString() : '—'}
          loading={isLoading}
        />
        <StatCard
          icon={<TrendingUp className="w-5 h-5 text-green-500" />}
          label={`Discovered (${period}d)`}
          value={d ? d.discovery.periodNew.toLocaleString() : '—'}
          sub={d ? `${d.discovery.last24Hours} in last 24h` : undefined}
          loading={isLoading}
        />
        <StatCard
          icon={<DollarSign className="w-5 h-5 text-amber-500" />}
          label={`Cost (${period}d)`}
          value={d ? formatCost(d.costs.periodTotal) : '—'}
          sub={d ? `${formatCost(d.costs.today)} today` : undefined}
          loading={isLoading}
        />
        <StatCard
          icon={<Activity className="w-5 h-5 text-purple-500" />}
          label="Avg Daily Cost"
          value={d ? formatCost(d.costs.dailyAverage) : '—'}
          loading={isLoading}
        />
      </div>

      {/* Data health */}
      {d && <DataHealthSection health={d.health} />}

      {/* Cost by day */}
      {d && d.costs.byDay.length > 0 && (
        <Section title="Cost by Day">
          <table className="min-w-full divide-y divide-gray-200 dark:divide-gray-700">
            <thead className="bg-gray-50 dark:bg-gray-800">
              <tr>
                <th className="px-4 py-2 text-left text-xs font-medium text-gray-500 dark:text-gray-400 uppercase">Date</th>
                <th className="px-4 py-2 text-right text-xs font-medium text-gray-500 dark:text-gray-400 uppercase">Cost</th>
              </tr>
            </thead>
            <tbody className="divide-y divide-gray-200 dark:divide-gray-700">
              {d.costs.byDay.map(row => (
                <tr key={row.date} className="hover:bg-gray-50 dark:hover:bg-gray-800/50">
                  <td className="px-4 py-2 text-sm text-gray-900 dark:text-gray-100">{row.date}</td>
                  <td className="px-4 py-2 text-sm text-right text-gray-900 dark:text-gray-100">{formatCost(row.cost)}</td>
                </tr>
              ))}
            </tbody>
          </table>
        </Section>
      )}

      {/* Recent pipeline runs */}
      {d && d.recentRuns.length > 0 && (
        <Section title="Recent Pipeline Runs">
          <table className="min-w-full divide-y divide-gray-200 dark:divide-gray-700">
            <thead className="bg-gray-50 dark:bg-gray-800">
              <tr>
                <th className="px-4 py-2 text-left text-xs font-medium text-gray-500 dark:text-gray-400 uppercase">Time</th>
                <th className="px-4 py-2 text-left text-xs font-medium text-gray-500 dark:text-gray-400 uppercase">Type</th>
                <th className="px-4 py-2 text-left text-xs font-medium text-gray-500 dark:text-gray-400 uppercase">Status</th>
                <th className="px-4 py-2 text-right text-xs font-medium text-gray-500 dark:text-gray-400 uppercase">Cost</th>
              </tr>
            </thead>
            <tbody className="divide-y divide-gray-200 dark:divide-gray-700">
              {d.recentRuns.map((run, i) => {
                const status = formatStatus(run.status);
                return (
                  <tr key={i} className="hover:bg-gray-50 dark:hover:bg-gray-800/50">
                    <td className="px-4 py-2 text-sm text-gray-900 dark:text-gray-100">{formatDateTime(run.startedAt)}</td>
                    <td className="px-4 py-2 text-sm text-gray-900 dark:text-gray-100">{formatRunType(run.type, run.platform)}</td>
                    <td className="px-4 py-2 text-sm">
                      <span className={`inline-flex items-center px-2 py-0.5 rounded text-xs font-medium ${status.className}`}>
                        {status.label}
                      </span>
                    </td>
                    <td className="px-4 py-2 text-sm text-right text-gray-900 dark:text-gray-100">{formatCost(run.cost)}</td>
                  </tr>
                );
              })}
            </tbody>
          </table>
        </Section>
      )}

      {/* Discovery by day */}
      {d && d.discovery.byDay.length > 0 && (
        <Section title="Discovery by Day">
          <table className="min-w-full divide-y divide-gray-200 dark:divide-gray-700">
            <thead className="bg-gray-50 dark:bg-gray-800">
              <tr>
                <th className="px-4 py-2 text-left text-xs font-medium text-gray-500 dark:text-gray-400 uppercase">Date</th>
                <th className="px-4 py-2 text-right text-xs font-medium text-gray-500 dark:text-gray-400 uppercase">New Records</th>
              </tr>
            </thead>
            <tbody className="divide-y divide-gray-200 dark:divide-gray-700">
              {d.discovery.byDay.map(row => (
                <tr key={row.date} className="hover:bg-gray-50 dark:hover:bg-gray-800/50">
                  <td className="px-4 py-2 text-sm text-gray-900 dark:text-gray-100">{row.date}</td>
                  <td className="px-4 py-2 text-sm text-right text-gray-900 dark:text-gray-100">{row.count.toLocaleString()}</td>
                </tr>
              ))}
            </tbody>
          </table>
        </Section>
      )}
    </div>
  );
};

// ── Sub-components ─────────────────────────────────────────────────────

function StatCard({ icon, label, value, sub, loading }: {
  icon: React.ReactNode;
  label: string;
  value: string;
  sub?: string;
  loading: boolean;
}) {
  return (
    <div className="card p-4">
      <div className="flex items-center gap-2 mb-1">
        {icon}
        <p className="text-sm text-gray-500 dark:text-gray-400">{label}</p>
      </div>
      {loading ? (
        <div className="h-8 w-24 bg-gray-200 dark:bg-gray-700 rounded animate-pulse mt-1" />
      ) : (
        <>
          <p className="text-2xl font-bold text-gray-900 dark:text-gray-100">{value}</p>
          {sub && <p className="text-xs text-gray-400 dark:text-gray-500 mt-0.5">{sub}</p>}
        </>
      )}
    </div>
  );
}

function DataHealthSection({ health }: { health: ScrapingDashboardData['health'] }) {
  return (
    <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
      {/* Data completeness */}
      <Section title="Data Completeness">
        <div className="space-y-3 p-4">
          <HealthBar label="Has Goal Amount" value={health.withGoal} total={health.totalActive} />
          <HealthBar label="Has Year" value={health.withYear} total={health.totalActive} />
          <HealthBar label="Has State" value={health.withState} total={health.totalActive} />
          <HealthBar label="Matched to Org" value={health.matchedToOrg} total={health.totalActive} />
        </div>
      </Section>

      {/* Platform & source breakdown */}
      <div className="space-y-4">
        <Section title="By Platform">
          <div className="p-4 space-y-1">
            {Object.entries(health.byPlatform).map(([platform, count]) => (
              <div key={platform} className="flex justify-between text-sm">
                <span className="text-gray-600 dark:text-gray-400">{PLATFORM_LABELS[platform] || platform}</span>
                <span className="text-gray-900 dark:text-gray-100 font-medium">{count.toLocaleString()}</span>
              </div>
            ))}
          </div>
        </Section>
        <Section title="By Source">
          <div className="p-4 space-y-1">
            {Object.entries(health.bySource).map(([source, count]) => (
              <div key={source} className="flex justify-between text-sm">
                <span className="text-gray-600 dark:text-gray-400">{SOURCE_LABELS[source] || source}</span>
                <span className="text-gray-900 dark:text-gray-100 font-medium">{count.toLocaleString()}</span>
              </div>
            ))}
          </div>
        </Section>
      </div>
    </div>
  );
}

function HealthBar({ label, value, total }: { label: string; value: number; total: number }) {
  const pct = total > 0 ? Math.round((value / total) * 100) : 0;
  return (
    <div>
      <div className="flex justify-between text-sm mb-1">
        <span className="text-gray-600 dark:text-gray-400">{label}</span>
        <span className="text-gray-900 dark:text-gray-100 font-medium">{pct}% ({value.toLocaleString()})</span>
      </div>
      <div className="w-full bg-gray-200 dark:bg-gray-700 rounded-full h-2">
        <div
          className={`h-2 rounded-full ${pct >= 75 ? 'bg-green-500' : pct >= 50 ? 'bg-yellow-500' : 'bg-red-500'}`}
          style={{ width: `${pct}%` }}
        />
      </div>
    </div>
  );
}

function Section({ title, children }: { title: string; children: React.ReactNode }) {
  return (
    <div className="card overflow-hidden">
      <div className="px-4 py-3 border-b border-gray-200 dark:border-gray-700">
        <h3 className="text-sm font-medium text-gray-700 dark:text-gray-300">{title}</h3>
      </div>
      {children}
    </div>
  );
}
